import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";
import {
  EmailTemplate,
  EmailTemplatePayload,
} from "../features/settings/templates/emailTemplates/emailTemplates.types";

type EmailTemplateApiModel = {
  id: string;
  relayId?: string;
  createdAt?: string;
  createdTimeUTC?: string;
  editedTimeUTC?: string;
  templateName?: string;
  name?: string;
  description?: string;
  subject: string;
  isActive: boolean;
  body: string;
  orgId?: string;
  createdByUserId?: string;
};

type EmailTemplateRequestBody = EmailTemplateApiModel;

const mapEmailTemplate = (template: EmailTemplateApiModel): EmailTemplate => ({
  id: template.id,
  name: template.templateName || template.name || "",
  description: template.description || "",
  subject: template.subject || "",
  body: template.body || "",
  isActive: template.isActive,
  updatedAt:
    template.editedTimeUTC ||
    template.createdTimeUTC ||
    template.createdAt ||
    new Date().toISOString(),
  orgId: template.orgId,
  createdByUserId: template.createdByUserId,
});

const buildEmailTemplateBody = ({
  values,
  orgId,
  userId,
  template,
}: {
  values: EmailTemplatePayload;
  orgId: string;
  userId: string;
  template?: EmailTemplate;
}): EmailTemplateRequestBody => ({
  id: template?.id || "00000000-0000-0000-0000-000000000000",
  relayId: "",
  createdAt: template?.updatedAt || new Date().toISOString(),
  createdTimeUTC: template?.updatedAt || new Date().toISOString(),
  editedTimeUTC: new Date().toISOString(),
  templateName: values.name.trim(),
  description: values.description?.trim() || "",
  subject: values.subject.trim(),
  isActive: values.isActive ?? template?.isActive ?? true,
  body: values.body,
  orgId,
  createdByUserId: template?.createdByUserId || userId,
});

export const emailTemplatesSlice = createApi({
  reducerPath: "emailTemplatesSlice",
  tagTypes: ["EmailTemplate"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();
      headers.set("Authorization", authHeader.Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getEmailTemplates: build.query<EmailTemplate[], { orgId: string }>({
      query: ({ orgId }) => ({
        url: "api/email-template/get-all",
        params: { orgId },
        method: "GET",
      }),
      transformResponse: (response: EmailTemplateApiModel[]) =>
        response.map(mapEmailTemplate),
      providesTags: ["EmailTemplate"],
    }),
    getEmailTemplateById: build.query<EmailTemplate, { id: string }>({
      query: ({ id }) => ({
        url: "api/email-template/get-by-Id",
        params: { id },
        method: "GET",
      }),
      transformResponse: mapEmailTemplate,
      providesTags: ["EmailTemplate"],
    }),
    addEmailTemplate: build.mutation<
      EmailTemplate,
      { values: EmailTemplatePayload; orgId: string; userId: string }
    >({
      query: ({ values, orgId, userId }) => ({
        url: "api/email-template/add",
        body: buildEmailTemplateBody({ values, orgId, userId }),
        method: "POST",
      }),
      transformResponse: mapEmailTemplate,
      invalidatesTags: ["EmailTemplate"],
    }),
    updateEmailTemplate: build.mutation<
      EmailTemplate,
      {
        template: EmailTemplate;
        values: EmailTemplatePayload;
        orgId: string;
        userId: string;
      }
    >({
      query: ({ template, values, orgId, userId }) => ({
        url: "api/email-template/update",
        body: buildEmailTemplateBody({ values, orgId, userId, template }),
        method: "PUT",
      }),
      transformResponse: mapEmailTemplate,
      invalidatesTags: ["EmailTemplate"],
    }),
    markEmailTemplateActive: build.mutation<
      unknown,
      { id: string; isActive: boolean }
    >({
      query: (params) => ({
        url: "api/email-template/mark-active",
        params,
        method: "PUT",
      }),
      invalidatesTags: ["EmailTemplate"],
    }),
    deleteEmailTemplate: build.mutation<unknown, { id: string; orgId: string }>(
      {
        query: (params) => ({
          url: "api/email-template",
          params,
          method: "DELETE",
        }),
        invalidatesTags: ["EmailTemplate"],
      },
    ),
  }),
});

export const {
  useGetEmailTemplatesQuery,
  useLazyGetEmailTemplateByIdQuery,
  useAddEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
  useMarkEmailTemplateActiveMutation,
  useDeleteEmailTemplateMutation,
} = emailTemplatesSlice;
